// Файл ExcelViewer.js

/**
 * Модуль «Excel Viewer» содержит логику, относящуюся к разделу «Document Attachments».
 * Он объединяет связанные обработчики, данные или элементы интерфейса и предоставляет их соседним модулям приложения.
 * Такое разделение упрощает сопровождение, тестирование и повторное использование этой части кода.
 */

//
// Просмотр .xlsx / .xls через SheetJS (xlsx). Только чтение,
// показываем всё содержимое; для больших файлов строки
// подгружаются порциями («показать ещё»).

import { useEffect, useState } from 'react';
import * as XLSX from 'xlsx';


const ROWS_STEP = 200;


// Буквенное имя столбца как в Excel: 0 → A, 25 → Z, 26 → AA …
const columnLetter = (index) => {
    let n = index + 1;
    let label = '';

    while (n > 0) {
        const rem = (n - 1) % 26;
        label = String.fromCharCode(65 + rem) + label;
        n = Math.floor((n - 1) / 26);
    }

    return label;
};


const ExcelViewer = ({ blob }) => {
    const [sheets, setSheets] = useState(null);
    const [activeSheet, setActiveSheet] = useState(0);
    const [visibleRows, setVisibleRows] = useState(ROWS_STEP);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!blob)
            return;

        let cancelled = false;

        blob.arrayBuffer()
            .then((buffer) => {
                if (cancelled)
                    return;

                const workbook = XLSX.read(
                    new Uint8Array(buffer), { type: 'array' });

                const parsed = workbook.SheetNames.map((name) => ({
                    name,
                    rows: XLSX.utils.sheet_to_json(
                        workbook.Sheets[name],
                        { header: 1, defval: '', blankrows: false }
                    ),
                }));

                setSheets(parsed);
                setActiveSheet(0);
                setVisibleRows(ROWS_STEP);
            })
            .catch(() => {
                if (!cancelled)
                    setError('Не удалось прочитать таблицу');
            });

        return () => {
            cancelled = true;
        };
    }, [blob]);

    if (error) {
        return (
            <div className="file-viewer__message flex-column flex-align-center file-viewer__message--error">
                {error}
            </div>
        );
    }

    if (!sheets)
        return null;

    if (sheets.length === 0) {
        return (
            <div className="file-viewer__message flex-column flex-align-center">
                Пустой файл
            </div>
        );
    }

    const sheet = sheets[activeSheet] || sheets[0];
    const rows = sheet.rows || [];
    const shownRows = rows.slice(0, visibleRows);
    const colCount = rows.reduce(
        (max, row) => Math.max(max, row.length), 0);

    return (
        <div className="excel-viewer flex-column">
            {sheets.length > 1 && (
                <div className="excel-viewer__tabs">
                    {sheets.map((s, i) => (
                        <button
                            key={`${s.name}-${i}`}
                            type="button"
                            className={`excel-viewer__tab ${
                                i === activeSheet ? 'is-active' : ''
                            }`}
                            onClick={() => {
                                setActiveSheet(i);
                                setVisibleRows(ROWS_STEP);
                            }}
                        >
                            {s.name}
                        </button>
                    ))}
                </div>
            )}

            <div className="excel-viewer__table-wrap">
                <table className="excel-viewer__table">
                    <thead>
                        <tr>
                            <th className="excel-viewer__corner" />
                            {Array.from(
                                { length: colCount },
                                (_, ci) => (
                                    <th
                                        key={ci}
                                        className="excel-viewer__colhead"
                                    >
                                        {columnLetter(ci)}
                                    </th>
                                )
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {shownRows.map((row, ri) => (
                            <tr key={ri}>
                                <th className="excel-viewer__rowhead">
                                    {ri + 1}
                                </th>
                                {Array.from(
                                    { length: colCount },
                                    (_, ci) => (
                                        <td key={ci}>
                                            {row[ci] !== undefined
                                                && row[ci] !== null
                                                ? String(row[ci])
                                                : ''}
                                        </td>
                                    )
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {rows.length > visibleRows && (
                <button
                    type="button"
                    className="file-viewer__download-btn excel-viewer__more"
                    onClick={() =>
                        setVisibleRows((v) => v + ROWS_STEP)}
                >
                    Показать ещё ({rows.length - visibleRows})
                </button>
            )}
        </div>
    );
};


export default ExcelViewer;
